import { useCallback, useState } from "react";
import { toast } from "@/components/ui/notify";
import { getFriendlyErrorMessage } from "@/lib/api-client";
import { useCreateAutoBatch } from "@/features/simulations/hooks";
import { toBatchInput } from "@/features/simulations/hooks-start-simulation";
import type { SimulationObjectiveChoice } from "@/types/sim-sandbox.types";

// A ÚNICA ação de simular do cenário, no lugar de "Rodar" +
// "Sugerir melhorias". As duas conviviam na mesma barra com regras diferentes — uma salvava e
// executava, a outra exigia que o cenário já estivesse salvo e recusava com um aviso — sem nada na
// tela explicando por quê.
// Simular SEMPRE persiste a estrutura corrente antes de executar: o que o usuário mexeu entra na
// conta, e a regra "salve antes de sugerir" deixa de existir.
// Uma execução = UM lote: a resposta da configuração atual sai primeiro e as variações vão
// preenchendo depois, ranqueadas pelo objetivo escolhido.

interface Params {
  scenarioId: string | undefined;
  objective: SimulationObjectiveChoice | null;
  /** Persiste a estrutura do rascunho; lança em caso de falha. */
  saveStructure: () => Promise<unknown>;
}

export function useSimulate({ scenarioId, objective, saveStructure }: Params) {
  const createBatch = useCreateAutoBatch();
  const [batchId, setBatchId] = useState<string | null>(null);

  const simulate = useCallback(async () => {
    if (!scenarioId || !objective) return;
    try {
      await saveStructure();
      const batch = await createBatch.mutateAsync({
        scenarioId,
        ...toBatchInput(objective),
        variationPct: 10,
        candidateCount: 6,
      });
      setBatchId(batch.id);
    } catch (err) {
      toast.error("Não foi possível simular.", { description: getFriendlyErrorMessage(err) });
    }
  }, [scenarioId, objective, saveStructure, createBatch]);

  return {
    simulate,
    batchId,
    clearBatch: () => setBatchId(null),
    isPending: createBatch.isPending,
  };
}

const ACTIVE_BATCH = new Set(["QUEUED", "RUNNING"]);

/**
 * O botão Simular fica travado enquanto a rodada está EM VOO — o que é o **estado** do lote, não a
 * existência dele.
 *
 * Derivar de `!!batchId` (como nasceu) travava o botão para sempre assim que uma resposta aparecia
 * na tela: o lote recém-executado continua ali, e reabrir um cenário salvo restaura o último lote
 * de propósito. O resultado era que "editar e simular de novo" — o passo 2 do roteiro de
 * aceitação — ficava impossível na segunda rodada em diante.
 *
 * Status ainda desconhecido conta como em voo: é o que impede um duplo disparo na janela entre
 * criar o lote e a primeira leitura. Com a leitura falhando, não: aí o erro já está na faixa de
 * resposta, e travar o botão sem explicação seria pior.
 */
export function isSimulationRunning(input: {
  pending: boolean;
  batchId: string | null;
  status: string | null | undefined;
  isError: boolean;
}): boolean {
  if (input.pending) return true;
  if (!input.batchId) return false;
  if (input.status == null) return !input.isError;
  return ACTIVE_BATCH.has(input.status);
}
